"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Brain, ChevronLeft, CheckCircle, XCircle, Trophy, RotateCcw, ArrowRight, Lightbulb } from "lucide-react";
import { ThemeToggle } from "@/lib/theme";
import { SAMPLE_WORDS } from "@/lib/vocab/sampleData";

interface QuizQuestion {
  wordId: string;
  word: string;
  correctDef: string;
  mnemonic: string;
  options: string[];
  correctIndex: number;
  mode: "word-to-def" | "def-to-word";
}

function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5);
}

function buildQuestions(): QuizQuestion[] {
  const pool = SAMPLE_WORDS.filter(w => w.status !== "mastered");
  return pool.map(w => {
    const distractors = shuffle(SAMPLE_WORDS.filter(x => x.id !== w.id))
      .slice(0, 3)
      .map(x => x.shortDef);
    const allOptions = shuffle([w.shortDef, ...distractors]);
    return {
      wordId: w.id,
      word: w.word,
      correctDef: w.shortDef,
      mnemonic: w.mnemonic,
      options: allOptions,
      correctIndex: allOptions.indexOf(w.shortDef),
      mode: "word-to-def",
    };
  });
}

export default function QuizPage() {
  // The quiz is shuffled with Math.random, so it must be built on the client only: building it during
  // server rendering produced a different order than the browser's first render (React error #418,
  // seen by real users on this page).
  const [questions, setQuestions] = useState<ReturnType<typeof buildQuestions> | null>(null);
  useEffect(() => { setQuestions(buildQuestions()); }, []);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [results, setResults] = useState<{ correct: boolean; word: string }[]>([]);
  const [done, setDone] = useState(false);
  const [showMnemonic, setShowMnemonic] = useState(false);

  if (!questions) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">Preparing your quiz…</div>
    );
  }
  const q = questions[current];
  const answered = selected !== null;
  const isCorrect = selected === q?.correctIndex;

  function handleSelect(idx: number) {
    if (answered) return;
    setSelected(idx);
    const correct = idx === q.correctIndex;
    setResults(r => [...r, { correct, word: q.word }]);
    if (!correct) setShowMnemonic(true);
  }

  function next() {
    if (!questions || current + 1 >= questions.length) {
      setDone(true);
    } else {
      setCurrent(c => c + 1);
      setSelected(null);
      setShowMnemonic(false);
    }
  }

  if (done) {
    const correctCount = results.filter(r => r.correct).length;
    const pct = Math.round((correctCount / results.length) * 100);
    const graduated = results.filter(r => r.correct).map(r => r.word);
    const toReview = results.filter(r => !r.correct).map(r => r.word);

    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--bg-primary)" }}>
        <nav className="border-b" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-card)" }}>
          <div className="max-w-xl mx-auto px-4 h-14 flex items-center justify-between">
            <Link href="/vocab" className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
              <Brain size={16} className="text-blue-500" /> VocabMaster
            </Link>
            <ThemeToggle />
          </div>
        </nav>
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-md">
            {/* Score */}
            <div className="text-center mb-6">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl font-bold
                ${pct >= 80 ? "bg-green-100 text-green-600" : pct >= 60 ? "bg-yellow-100 text-yellow-600" : "bg-red-100 text-red-600"}`}>
                {pct}%
              </div>
              <h2 className="text-2xl font-bold mb-1" style={{ color: "var(--text-primary)" }}>
                {pct >= 80 ? "Excellent!" : pct >= 60 ? "Good progress!" : "Keep practicing!"}
              </h2>
              <p style={{ color: "var(--text-muted)" }}>{correctCount} / {results.length} correct</p>
            </div>

            {/* Graduated */}
            {graduated.length > 0 && (
              <div className="rounded-2xl border p-4 mb-3" style={{ backgroundColor: "var(--bg-card)", borderColor: "var(--border)" }}>
                <div className="flex items-center gap-2 mb-2">
                  <Trophy size={16} className="text-green-500" />
                  <p className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>
                    Graduated ({graduated.length} words)
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {graduated.map(w => (
                    <span key={w} className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-medium">{w}</span>
                  ))}
                </div>
              </div>
            )}

            {/* To review */}
            {toReview.length > 0 && (
              <div className="rounded-2xl border p-4 mb-5" style={{ backgroundColor: "var(--bg-card)", borderColor: "var(--border)" }}>
                <div className="flex items-center gap-2 mb-2">
                  <RotateCcw size={16} className="text-orange-500" />
                  <p className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>
                    Back to pool ({toReview.length} words)
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {toReview.map(w => (
                    <span key={w} className="text-xs px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 font-medium">{w}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <Link href="/vocab/study"
                className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-center">
                Study Missed Words <ArrowRight size={16} />
              </Link>
              <Link href="/vocab/pool"
                className="w-full py-3 rounded-xl font-medium text-sm text-center hover:opacity-80"
                style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" }}>
                Back to Pool
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--bg-primary)" }}>
      {/* Nav */}
      <nav className="border-b" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-card)" }}>
        <div className="max-w-xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link href="/vocab/pool" className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
            <ChevronLeft size={16} /> Pool
          </Link>
          <span className="text-sm font-medium" style={{ color: "var(--text-muted)" }}>
            {current + 1} / {questions.length}
          </span>
          <ThemeToggle />
        </div>
      </nav>

      {/* Progress */}
      <div className="h-1" style={{ backgroundColor: "var(--bg-secondary)" }}>
        <div className="h-1 bg-blue-500 transition-all duration-300"
          style={{ width: `${(current / questions.length) * 100}%` }} />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">

          {/* Question */}
          <div className="rounded-2xl border p-6 mb-4" style={{ backgroundColor: "var(--bg-card)", borderColor: "var(--border)" }}>
            <p className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--text-muted)" }}>
              What does this word mean?
            </p>
            <h2 className="text-4xl font-bold mb-1" style={{ color: "var(--text-primary)" }}>{q.word}</h2>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>Choose the correct definition</p>
          </div>

          {/* Options */}
          <div className="space-y-2 mb-4">
            {q.options.map((opt, idx) => {
              let cls = "border hover:border-blue-400 hover:bg-blue-50 cursor-pointer";
              let style = { borderColor: "var(--border)", backgroundColor: "var(--bg-card)" };

              if (answered) {
                if (idx === q.correctIndex) {
                  cls = "border-2 border-green-500 bg-green-50 cursor-default";
                  style = {} as typeof style;
                } else if (idx === selected) {
                  cls = "border-2 border-red-400 bg-red-50 cursor-default";
                  style = {} as typeof style;
                } else {
                  cls = "border opacity-50 cursor-default";
                }
              }

              return (
                <button
                  key={idx}
                  onClick={() => handleSelect(idx)}
                  className={`w-full text-left px-4 py-3.5 rounded-xl text-sm transition-all flex items-center justify-between ${cls}`}
                  style={style}
                >
                  <span style={{ color: answered && idx === q.correctIndex ? "#16a34a" : answered && idx === selected ? "#dc2626" : "var(--text-primary)" }}>
                    {opt}
                  </span>
                  {answered && idx === q.correctIndex && <CheckCircle size={16} className="text-green-500 flex-shrink-0" />}
                  {answered && idx === selected && idx !== q.correctIndex && <XCircle size={16} className="text-red-500 flex-shrink-0" />}
                </button>
              );
            })}
          </div>

          {/* Feedback */}
          {answered && (
            <div className="mb-4">
              <div className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isCorrect ? "text-green-600" : "text-red-500"}`}>
                {isCorrect ? <><CheckCircle size={15} /> Correct!</> : <><XCircle size={15} /> Not quite — but here's a hint to remember it:</>}
              </div>
              {(!isCorrect || showMnemonic) && (
                <div className="rounded-xl p-3 flex gap-2" style={{ backgroundColor: "#fefce8", border: "1px solid #fef08a" }}>
                  <Lightbulb size={15} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm italic text-yellow-900">{q.mnemonic}</p>
                </div>
              )}
            </div>
          )}

          {/* Next */}
          {answered && (
            <button onClick={next}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
              {current + 1 >= questions.length ? "See Results" : "Next Word"}
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
